import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigServiceProvider } from '../config-service/config-service';
import { StorageServiceProvider } from '../storage-service/storage-service';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { App } from 'ionic-angular';
import { catchError, flatMap, mergeMap, shareReplay, tap } from 'rxjs/operators';

export interface AuthTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  refresh_token: string;
  token_type: string;
  id_token: string;
  session_state: string;
  scope: string;
}

@Injectable()
export class AuthenticationServiceProvider {
  private _stateSubject: Subject<boolean> = new Subject<boolean>();
  readonly state$: Observable<boolean> = this._stateSubject.asObservable();
  refreshToken;

  constructor(
    private http: HttpClient,
    private configService: ConfigServiceProvider,
    private storageService: StorageServiceProvider,
    private app: App,
  ) {
    this.onInit();
  }

  private onInit(): void {
    this.state$.subscribe((loggedIn) => {
      if (loggedIn === true) {
        this.onAuthSucceded();
      } else {
        this.onAuthFailed();
      }
    });
  }

  getAccessToken(): Promise<any> {
    return this.storageService
      .get('accessToken')
      .then((res) => {
        if (res === null) {
          return Promise.reject('Access token missing');
        }
        return res;
      })
      .catch((err) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          this.renewTokens()
            .toPromise()
            .then((res) => res);
        }
        throw err;
      });
  }

  validateTokens(): Promise<void> {
    return this.storageService
      .get('accessToken')
      .then((res) => res === null && Promise.reject('access token missing'))
      .then(() => this.http.get(this.configService.getUrl('account')).toPromise())
      .then(() => null)
      .catch((err) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          return this.renewTokens();
        }
        throw err;
      });
  }

  exchangeCodeForToken(authorizationCode: string): Promise<void> {
    let urlEncodedBody = new URLSearchParams();
    urlEncodedBody.append('client_secret', this.configService.AUTH_CLIENT_SECRET);
    urlEncodedBody.append('client_id', this.configService.AUTH_CLIENT_ID);
    urlEncodedBody.append('grant_type', 'authorization_code');
    urlEncodedBody.append('redirect_uri', this.configService.AUTH_TOKEN_REDIRECT);
    urlEncodedBody.append('code', authorizationCode);

    return this.http
      .post<AuthTokenResponse>(this.configService.TOKEN_URL, urlEncodedBody.toString(), {
        headers: {
          'Content-type': 'application/x-www-form-urlencoded',
        },
        withCredentials: true,
      })
      .toPromise()
      .then(
        (response) => {
          return Promise.all([
            this.storageService.set('accessToken', response.access_token),
            this.storageService.set('refreshToken', response.refresh_token),
          ]);
        },
        (reason) => {},
      )
      .then(() => {
        this._stateSubject.next(true);
      });
  }

  exchangeUserCredsForToken(user): Promise<void> {
    const urlEncodedBody = new URLSearchParams();
    urlEncodedBody.append('client_secret', this.configService.AUTH_CLIENT_SECRET);
    urlEncodedBody.append('client_id', this.configService.AUTH_CLIENT_ID);
    urlEncodedBody.append('grant_type', 'password');
    urlEncodedBody.append('username', user.username);
    urlEncodedBody.append('password', user.password);

    return this.http
      .post<any>(this.configService.TOKEN_URL, urlEncodedBody.toString(), {
        headers: {
          'Content-type': 'application/x-www-form-urlencoded; charset=utf-8',
        },
      })
      .toPromise()
      .then((response) =>
        Promise.all([this.storageService.set('accessToken', response.access_token), this.storageService.set('refreshToken', response.refresh_token)]),
      )
      .then(() => {
        this._stateSubject.next(true);
      });
  }

  logout() {
    this.storageService.set('accessToken', null);
    this.storageService.set('refreshToken', null);
    this._stateSubject.next(false);
  }

  resetPassword(email: string): Promise<void> {
    const params = new URLSearchParams();
    params.append('client_secret', this.configService.getConfig('clientSecret'));

    return this.http
      .post(this.configService.getUrl('resetPassword'), email, {
        headers: {
          'Content-type': 'application/x-www-form-urlencoded; charset=utf-8',
        },
      })
      .toPromise()
      .then(() => null);
  }

  renewTokens(): Observable<string> {
    return Observable.fromPromise(this.storageService.get('refreshToken')).pipe(
      flatMap((refreshToken: string) => {
        if (refreshToken === null) {
          return Promise.reject(null);
        }

        const params = new URLSearchParams();
        params.append('client_secret', this.configService.getConfig('clientSecret'));
        params.append('client_id', this.configService.getConfig('clientId'));
        params.append('grant_type', 'refresh_token');
        params.append('refresh_token', refreshToken);
        return this.http
          .post(this.configService.TOKEN_URL, params.toString(), {
            headers: {
              'Content-type': 'application/x-www-form-urlencoded; charset=utf-8',
            },
          })
          .toPromise();
      }),
      tap((response: any) => {
        this.storageService.set('refreshToken', response.refresh_token);
      }),
      mergeMap((response) => this.storageService.set('accessToken', response.access_token)),
      catchError(() => {
        this.logout();
        return Observable.throw('Renew token is invalid');
      }),
      tap(() => console.log('[AuthService] Token refreshed')),
      shareReplay(),
    );
  }

  private onAuthSucceded() {}

  private onAuthFailed() {
    this.app.getActiveNavs()[0].setRoot('LoginPage');
  }
}



// WEBPACK FOOTER //
// ./src/modules/core/services/authentication-service/authentication-service.ts